import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Eye, Send, Package, Trash2, ArrowLeft, FileText } from 'lucide-react';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PurchaseOrdersPage() {
  const [pos, setPOs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [formData, setFormData] = useState({
    vendor_id: '',
    expected_date: '',
    notes: '',
    lines: []
  });

  const { token = localStorage.getItem('backoffice_token') } = useOutletContext() || {};
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useLatestCallback(async () => {
    setLoading(true);
    try {
      const [posRes, vendorsRes, itemsRes] = await Promise.all([
        fetch(`${API_URL}/api/inv/purchase-orders?limit=100`, { headers }),
        fetch(`${API_URL}/api/inv/vendors`, { headers }),
        fetch(`${API_URL}/api/inv/items?limit=200`, { headers })
      ]);
      
      if (posRes.ok) setPOs(await posRes.json());
      if (vendorsRes.ok) setVendors(await vendorsRes.json());
      if (itemsRes.ok) setItems(await itemsRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePO = async (e) => {
    e.preventDefault();
    if (formData.lines.length === 0) {
      alert('Please add at least one item');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/inv/purchase-orders`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          vendor_id: formData.vendor_id,
          expected_date: formData.expected_date || null,
          notes: formData.notes,
          lines: formData.lines.map(line => ({
            item_id: line.item_id,
            ordered_qty_packs: parseFloat(line.qty),
            pack_qty_base: 1,
            price_per_pack: parseFloat(line.price)
          }))
        })
      });
      
      if (res.ok) {
        setShowDialog(false);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Error creating PO');
      }
    } catch (err) {
      console.error('Error creating PO:', err);
    }
  };

  const handleUpdateStatus = async (poId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/inv/purchase-orders/${poId}/status?status=${status}`, {
        method: 'PATCH',
        headers
      });
      if (res.ok) fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const viewPODetails = async (poId) => {
    try {
      const res = await fetch(`${API_URL}/api/inv/purchase-orders/${poId}`, { headers });
      if (res.ok) {
        setSelectedPO(await res.json());
      }
    } catch (err) {
      console.error('Error fetching PO details:', err);
    }
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { item_id: '', qty: 1, price: 0 }]
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    setFormData({ ...formData, lines: newLines });
  };

  const removeLine = (index) => {
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData({ ...formData, lines: newLines });
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      expected_date: '',
      notes: '',
      lines: []
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-gray-600',
      'SENT': 'bg-blue-600',
      'PARTIALLY_RECEIVED': 'bg-yellow-600',
      'RECEIVED': 'bg-green-600',
      'CLOSED': 'bg-purple-600',
      'CANCELED': 'bg-red-600'
    };
    return colors[status] || 'bg-gray-600';
  };

  const filteredPOs = pos.filter(po => 
    statusFilter === 'all' || po.status === statusFilter
  );

  const calculateTotal = () => {
    return formData.lines.reduce((sum, line) => {
      return sum + (parseFloat(line.qty) || 0) * (parseFloat(line.price) || 0);
    }, 0);
  };

  return (
    <div className="p-6 space-y-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <a href={bo('inventory')} className="p-2 hover:bg-gray-800 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </a>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">Purchase Orders</h1>
          <p className="text-gray-400">Create and manage purchase orders</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-po-btn">
              <Plus className="w-4 h-4 mr-2" /> Create PO
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create Purchase Order</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreatePO} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Vendor *</Label>
                  <Select
                    value={formData.vendor_id}
                    onValueChange={(v) => setFormData({ ...formData, vendor_id: v })}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Expected Date</Label>
                  <Input
                    type="date"
                    value={formData.expected_date}
                    onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })}
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
              </div>
              
              {/* Line Items */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-gray-300">Line Items</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addLine}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {formData.lines.map((line, index) => (
                    <div key={index} className="flex gap-2 items-center p-2 bg-gray-800/50 rounded">
                      <Select
                        value={line.item_id}
                        onValueChange={(v) => updateLine(index, 'item_id', v)}
                      >
                        <SelectTrigger className="flex-1 bg-gray-800 border-gray-700">
                          <SelectValue placeholder="Select item" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 max-h-[200px]">
                          {items.map(item => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name} {item.sku ? `(${item.sku})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={line.qty}
                        onChange={(e) => updateLine(index, 'qty', e.target.value)}
                        className="w-20 bg-gray-800 border-gray-700"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Price"
                        value={line.price}
                        onChange={(e) => updateLine(index, 'price', e.target.value)}
                        className="w-24 bg-gray-800 border-gray-700"
                      />
                      <span className="text-gray-400 w-20 text-right">
                        ${((parseFloat(line.qty) || 0) * (parseFloat(line.price) || 0)).toFixed(2)}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeLine(index)}
                        className="text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {formData.lines.length === 0 && (
                    <p className="text-gray-400 text-center py-4">No items added</p>
                  )}
                </div>
                
                <div className="flex justify-end pt-2 border-t border-gray-700">
                  <span className="text-lg font-semibold text-white">
                    Total: ${calculateTotal().toFixed(2)}
                  </span>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Order notes..."
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!formData.vendor_id || formData.lines.length === 0}>
                  Create PO
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-gray-800 border-gray-700">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="SENT">Sent</SelectItem>
            <SelectItem value="PARTIALLY_RECEIVED">Partially Received</SelectItem>
            <SelectItem value="RECEIVED">Received</SelectItem>
            <SelectItem value="CLOSED">Closed</SelectItem>
            <SelectItem value="CANCELED">Canceled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* PO Table */}
      <Card className="bg-gray-900/50 border-gray-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="text-left p-4 text-gray-400 font-medium">PO Number</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Vendor</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Date</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Expected</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Items</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Total</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Status</th>
                  <th className="text-center p-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPOs.map(po => (
                  <tr key={po.id} className="border-t border-gray-700 hover:bg-gray-800/30">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-400" />
                        <span className="font-medium text-white">{po.po_number}</span>
                      </div>
                    </td>
                    <td className="p-4 text-gray-300">{po.vendor_name || '-'}</td>
                    <td className="p-4 text-gray-400">
                      {new Date(po.order_date).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-gray-400">
                      {po.expected_date ? new Date(po.expected_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-4 text-right text-gray-400">{po.line_count}</td>
                    <td className="p-4 text-right text-white font-medium">
                      ${po.total_amount?.toFixed(2)}
                    </td>
                    <td className="p-4 text-center">
                      <Badge className={getStatusColor(po.status)}>{po.status}</Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => viewPODetails(po.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {po.status === 'DRAFT' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUpdateStatus(po.id, 'SENT')}
                            className="text-blue-400"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        {(po.status === 'SENT' || po.status === 'PARTIALLY_RECEIVED') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.location.href = `${bo('inventory/receiving')}?po=${po.id}`}
                            className="text-green-400"
                          >
                            <Package className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredPOs.length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-gray-400">
                      {loading ? 'Loading...' : 'No purchase orders found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* PO Details Dialog */}
      {selectedPO && (
        <Dialog open={!!selectedPO} onOpenChange={() => setSelectedPO(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {selectedPO.po_number}
                <Badge className={getStatusColor(selectedPO.status)}>{selectedPO.status}</Badge>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">Vendor</p>
                  <p className="text-white">{selectedPO.vendor_name}</p>
                </div>
                <div>
                  <p className="text-gray-400">Order Date</p>
                  <p className="text-white">{new Date(selectedPO.order_date).toLocaleDateString()}</p>
                </div>
                {selectedPO.expected_date && (
                  <div>
                    <p className="text-gray-400">Expected Date</p>
                    <p className="text-white">{new Date(selectedPO.expected_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
              
              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-white font-medium mb-2">Line Items</h4>
                <div className="space-y-2">
                  {selectedPO.lines?.map((line, i) => (
                    <div key={i} className="flex justify-between p-2 bg-gray-800/50 rounded">
                      <div>
                        <p className="text-white">{line.item_name}</p>
                        <p className="text-sm text-gray-400">
                          Ordered: {line.ordered_qty_packs} | Received: {line.received_qty_base}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-white">${line.line_total?.toFixed(2)}</p>
                        <p className="text-sm text-gray-400">${line.price_per_pack?.toFixed(2)} ea</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-between pt-4 border-t border-gray-700">
                <span className="text-gray-400">Total</span>
                <span className="text-xl font-bold text-white">${selectedPO.total_amount?.toFixed(2)}</span>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
